import { Link } from "react-router-dom";
import { useUiLang } from "../../stores/uiLang";

function FerpaStatementPageEn() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">FERPA Compliance Statement</h1>
        <p className="text-lg max-w-2xl mx-auto">
          Project Explore's obligations and practices under the Family
          Educational Rights and Privacy Act.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="text-xs text-gray-500">
          <strong>Statute:</strong> 20 U.S.C. § 1232g; 34 CFR Part 99 &nbsp;|&nbsp;
          <strong>Operator:</strong> Startup Smartup, 2380 California Street #402, San Francisco, CA 94115 &nbsp;|&nbsp;
          <strong>Effective Date:</strong> May 27, 2026
        </p>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            1. School Official Designation
          </h2>
          <p className="mb-3">
            Project Explore operates as a "school official with a legitimate
            educational interest" within the meaning of 34 CFR
            § 99.31(a)(1)(i)(B) when a school district contracts with Startup
            Smartup to deliver the Project Explore platform as an institutional
            service to students and teachers.
          </p>
          <p className="mb-3">Under this designation:</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
            <li>Startup Smartup accesses education records only to the extent necessary to perform the contracted educational services</li>
            <li>Startup Smartup is subject to FERPA's use and re-disclosure restrictions</li>
            <li>The contracting district remains the educational agency responsible for ensuring that FERPA requirements are met in connection with those education records</li>
          </ul>
          <p>
            <strong>Technical enforcement of the § 99.31(a)(1)(i)(B) scope
            limitation.</strong> Project Explore enforces the
            legitimate-educational-interest requirement through Row Level
            Security (RLS) policies that restrict each user's data access to
            records within their legitimate educational interest. Teachers see
            only their assigned students. School administrators see only their
            school's records. District administrators see only their district's
            records. Cross-district data access is technically prohibited at the
            database layer.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            2. Education Records Held
          </h2>
          <p className="mb-3">
            The following information maintained by Project Explore constitutes
            "education records" under FERPA and is treated with the protections
            required by that statute:
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-200 px-3 py-2 text-left font-medium">Data Element</th>
                  <th className="border border-gray-200 px-3 py-2 text-left font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Student first name and last name</td><td className="border border-gray-200 px-3 py-2"></td></tr>
                <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Student grade level</td><td className="border border-gray-200 px-3 py-2"></td></tr>
                <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Student email address</td><td className="border border-gray-200 px-3 py-2">Collected for grade 3+ only, where provided by the district</td></tr>
                <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Assignment responses and submissions</td><td className="border border-gray-200 px-3 py-2">All tiers (T1–T4)</td></tr>
                <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Assessment scores and grading feedback</td><td className="border border-gray-200 px-3 py-2">Including AI-generated feedback</td></tr>
                <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Video viewing analytics and completion data</td><td className="border border-gray-200 px-3 py-2">Per chapter and scene</td></tr>
                <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">IEP/504 accommodation flags</td><td className="border border-gray-200 px-3 py-2">Where applicable; see Section 3</td></tr>
                <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Parent/guardian contact information</td><td className="border border-gray-200 px-3 py-2">Where provided by the district</td></tr>
                <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Class enrollment records</td><td className="border border-gray-200 px-3 py-2">Linking student to teacher and class</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            3. Sensitive Records — IEP/504 Accommodation Flags
          </h2>
          <p className="mb-3">
            Student IEP/504 accommodation flags are treated as sensitive
            education records and receive stricter access controls within the
            platform. Access to this data is restricted to:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>The student's directly assigned teacher(s)</li>
            <li>The school administrator for the student's school</li>
            <li>The student's parent or guardian (upon request through the school admin)</li>
          </ul>
          <p>
            Other teachers within the same school cannot view accommodation
            records for students not assigned to them. Cross-teacher access to
            accommodation data is technically prohibited at the database layer.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            4. Directory Information
          </h2>
          <p>
            Project Explore does not designate any information as "directory
            information" under FERPA. All student data maintained by the
            platform is treated as an education record protected under FERPA.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            5. Parental Access Rights
          </h2>
          <p className="mb-3">
            Parents and eligible students have the right under 34 CFR § 99.10
            to inspect and review their student's education records maintained
            by Project Explore.
          </p>
          <p className="font-medium mb-2">Process:</p>
          <ol className="list-decimal list-inside space-y-2 ml-2 mb-3">
            <li>A parent or eligible student contacts their school administrator.</li>
            <li>The school administrator can export a student's records from the teacher dashboard or request an export from Project Explore support.</li>
            <li>Requests will be fulfilled within 45 days of receipt.</li>
          </ol>
          <p className="mb-3">
            Alternatively, a parent may contact Project Explore support directly,
            and Project Explore will coordinate with the contracting school to
            fulfill the request within 45 days.
          </p>
          <p>
            Where a parent disputes a District's response to a records access or
            correction request, Project Explore will cooperate with the
            District's resolution process. Project Explore is not the data
            controller and does not independently adjudicate disputes between
            parents and the contracting district.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            6. Disclosure Framework
          </h2>
          <p className="mb-3">
            Project Explore does not disclose education records to any third
            party except:
          </p>
          <p className="mb-2">
            <strong>(a) Back to the contracting district.</strong> Records are
            accessible to district administrators, school administrators, and
            teachers with a legitimate educational interest.
          </p>
          <p className="mb-2">
            <strong>(b) To sub-processors under data processing agreements.</strong>{" "}
            Each sub-processor operates under a DPA that prohibits use of
            education records for any purpose other than providing the contracted
            service. See the{" "}
            <Link to="/privacy" className="text-indigo-600 underline">Privacy Policy</Link>{" "}
            for the full vendor list.
          </p>
          <p className="mb-2">
            <strong>(c) Legal process.</strong> Project Explore will comply with
            a valid subpoena, court order, or other binding legal process. To
            the extent permitted by law, Project Explore will provide the
            contracting district with notice before complying.
          </p>
          <p className="mb-4">
            <strong>(d) Health and safety emergency.</strong> Disclosure may be
            made without consent where necessary to protect the health or safety
            of the student or others, in accordance with 34 CFR § 99.36.
          </p>
          <p className="font-medium mb-1">Project Explore does NOT:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Sell or rent education records</li>
            <li>Disclose education records to advertisers or marketing companies</li>
            <li>Use education records to build profiles for non-educational purposes</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            7. Annual Notification Support
          </h2>
          <p>
            FERPA requires schools to provide annual notification to parents of
            their FERPA rights. Project Explore supports contracting districts
            in meeting this obligation by providing standard language describing
            Project Explore's role and data practices that districts may include
            in their annual FERPA notification. Contact Project Explore support
            to request this language.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            8. Contact
          </h2>
          <p className="mb-2">
            For FERPA-related inquiries, including requests to inspect, review,
            or correct education records:
          </p>
          <p>
            <strong>Justin Ghiglia, Privacy Contact</strong>
            <br />
            Startup Smartup
            <br />
            2380 California Street #402, San Francisco, CA 94115
            <br />
            <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
              privacy@startupsmartup.com
            </a>
          </p>
        </div>

        <div className="text-xs text-gray-500">
          Effective Date: May 27, 2026
          <br />
          Cross-reference:{" "}
          <Link to="/privacy" className="text-indigo-600 underline">Privacy Policy</Link>{" | "}
          <Link to="/privacy#coppa" className="text-indigo-600 underline">COPPA Addendum</Link>{" | "}
          <Link to="/legal/sopipa" className="text-indigo-600 underline">SOPIPA Acknowledgment</Link>{" | "}
          <Link to="/legal/dpa" className="text-indigo-600 underline">Data Processing Agreement</Link>
        </div>
      </section>
    </div>
  );
}

const TH = "border border-gray-200 px-3 py-2 text-left font-medium";
const TD = "border border-gray-200 px-3 py-2";

function FerpaStatementPageEs() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">Declaración de cumplimiento de FERPA</h1>
        <p className="text-lg max-w-2xl mx-auto">
          Las obligaciones y prácticas de Project Explore bajo la Ley de
          Derechos Educativos y Privacidad de la Familia.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="p-3 bg-indigo-50 border-l-4 border-indigo-500 rounded text-indigo-800">
          Esta página es una traducción al español. Para ver el texto original en inglés, elige EN arriba.
        </p>
        <p className="text-xs text-gray-500">
          <strong>Ley:</strong> 20 U.S.C. § 1232g; 34 CFR Parte 99 &nbsp;|&nbsp;
          <strong>Operador:</strong> Startup Smartup, 2380 California Street #402, San Francisco, CA 94115 &nbsp;|&nbsp;
          <strong>Fecha de entrada en vigor:</strong> 27 de mayo de 2026
        </p>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            1. Designación como funcionario escolar
          </h2>
          <p className="mb-3">
            Project Explore actúa como "funcionario escolar con un interés
            educativo legítimo" en el sentido de 34 CFR § 99.31(a)(1)(i)(B)
            cuando un distrito escolar contrata a Startup Smartup para ofrecer
            la plataforma Project Explore como servicio institucional a
            estudiantes y maestros.
          </p>
          <p className="mb-3">Bajo esta designación:</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
            <li>Startup Smartup accede a los expedientes educativos solo en la medida necesaria para prestar los servicios educativos contratados</li>
            <li>Startup Smartup está sujeto a las restricciones de FERPA sobre el uso y la nueva divulgación de datos</li>
            <li>El distrito contratante sigue siendo la agencia educativa responsable de asegurar que se cumplan los requisitos de FERPA respecto de esos expedientes educativos</li>
          </ul>
          <p>
            <strong>Aplicación técnica del límite del § 99.31(a)(1)(i)(B).</strong>{" "}
            Project Explore hace cumplir el requisito de interés educativo
            legítimo mediante políticas de seguridad a nivel de fila (RLS) que
            limitan el acceso de cada usuario a los registros que
            corresponden a su interés educativo legítimo. Los maestros ven
            solo a los estudiantes que tienen asignados. Los administradores
            escolares ven solo los registros de su escuela. Los administradores
            del distrito ven solo los registros de su distrito. El acceso a
            datos de otros distritos está prohibido técnicamente en la capa de
            la base de datos.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            2. Expedientes educativos que se conservan
          </h2>
          <p className="mb-3">
            La siguiente información que mantiene Project Explore constituye
            "expedientes educativos" bajo FERPA y se trata con las protecciones
            que exige esa ley:
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className={TH}>Dato</th>
                  <th className={TH}>Notas</th>
                </tr>
              </thead>
              <tbody>
                <tr className="even:bg-gray-50"><td className={TD}>Nombre y apellido del estudiante</td><td className={TD}></td></tr>
                <tr className="even:bg-gray-50"><td className={TD}>Grado escolar del estudiante</td><td className={TD}></td></tr>
                <tr className="even:bg-gray-50"><td className={TD}>Correo electrónico del estudiante</td><td className={TD}>Se recopila solo de 3.er grado en adelante, cuando lo proporciona el distrito</td></tr>
                <tr className="even:bg-gray-50"><td className={TD}>Respuestas y trabajos entregados</td><td className={TD}>Todos los niveles (T1 a T4)</td></tr>
                <tr className="even:bg-gray-50"><td className={TD}>Calificaciones de evaluaciones y comentarios de calificación</td><td className={TD}>Incluye comentarios generados por IA</td></tr>
                <tr className="even:bg-gray-50"><td className={TD}>Estadísticas de reproducción de video y datos de finalización</td><td className={TD}>Por capítulo y escena</td></tr>
                <tr className="even:bg-gray-50"><td className={TD}>Indicadores de adaptaciones IEP/504</td><td className={TD}>Cuando corresponda; consulta la Sección 3</td></tr>
                <tr className="even:bg-gray-50"><td className={TD}>Información de contacto del padre, madre o tutor</td><td className={TD}>Cuando la proporciona el distrito</td></tr>
                <tr className="even:bg-gray-50"><td className={TD}>Registros de inscripción en clases</td><td className={TD}>Vinculan al estudiante con el maestro y la clase</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            3. Registros delicados: indicadores de adaptaciones IEP/504
          </h2>
          <p className="mb-3">
            Los indicadores de adaptaciones IEP/504 de los estudiantes se
            tratan como expedientes educativos delicados y tienen controles de
            acceso más estrictos dentro de la plataforma. El acceso a estos
            datos se limita a:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>El maestro o los maestros asignados directamente al estudiante</li>
            <li>El administrador de la escuela del estudiante</li>
            <li>El padre, madre o tutor del estudiante (si lo pide por medio del administrador escolar)</li>
          </ul>
          <p>
            Los demás maestros de la misma escuela no pueden ver los registros
            de adaptaciones de estudiantes que no tienen asignados. El acceso
            de un maestro a los datos de adaptaciones de otro maestro está
            prohibido técnicamente en la capa de la base de datos.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            4. Información de directorio
          </h2>
          <p>
            Project Explore no designa ninguna información como "información
            de directorio" bajo FERPA. Todos los datos de los estudiantes que
            mantiene la plataforma se tratan como un expediente educativo
            protegido por FERPA.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            5. Derechos de acceso de los padres
          </h2>
          <p className="mb-3">
            Los padres y los estudiantes elegibles tienen derecho, bajo 34 CFR
            § 99.10, a inspeccionar y revisar los expedientes educativos de su
            estudiante que mantiene Project Explore.
          </p>
          <p className="font-medium mb-2">Proceso:</p>
          <ol className="list-decimal list-inside space-y-2 ml-2 mb-3">
            <li>Un padre o estudiante elegible se comunica con el administrador de su escuela.</li>
            <li>El administrador escolar puede exportar los registros de un estudiante desde el panel del maestro o pedir una exportación al soporte de Project Explore.</li>
            <li>Las solicitudes se cumplirán en un plazo de 45 días después de recibirlas.</li>
          </ol>
          <p className="mb-3">
            También, un padre puede comunicarse directamente con el soporte de
            Project Explore, y Project Explore coordinará con la escuela
            contratante para cumplir la solicitud en un plazo de 45 días.
          </p>
          <p>
            Cuando un padre no esté de acuerdo con la respuesta de un Distrito
            a una solicitud de acceso o corrección de registros, Project
            Explore cooperará con el proceso de resolución del Distrito.
            Project Explore no es el controlador de los datos y no resuelve por
            su cuenta las disputas entre los padres y el distrito contratante.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            6. Marco de divulgación
          </h2>
          <p className="mb-3">
            Project Explore no divulga expedientes educativos a ningún tercero,
            salvo:
          </p>
          <p className="mb-2">
            <strong>(a) De vuelta al distrito contratante.</strong> Los
            registros están al alcance de los administradores del distrito, los
            administradores escolares y los maestros con un interés educativo
            legítimo.
          </p>
          <p className="mb-2">
            <strong>(b) A subprocesadores bajo acuerdos de procesamiento de datos.</strong>{" "}
            Cada subprocesador opera bajo un DPA que prohíbe usar los
            expedientes educativos para cualquier fin distinto de prestar el
            servicio contratado. Consulta la{" "}
            <Link to="/privacy" className="text-indigo-600 underline">Política de privacidad</Link>{" "}
            para ver la lista completa de proveedores.
          </p>
          <p className="mb-2">
            <strong>(c) Trámite legal.</strong> Project Explore cumplirá con un
            citatorio válido, una orden judicial u otro trámite legal
            obligatorio. En la medida que la ley lo permita, Project Explore
            avisará al distrito contratante antes de cumplir.
          </p>
          <p className="mb-4">
            <strong>(d) Emergencia de salud o seguridad.</strong> Se puede
            hacer una divulgación sin consentimiento cuando sea necesaria para
            proteger la salud o la seguridad del estudiante o de otras
            personas, de acuerdo con 34 CFR § 99.36.
          </p>
          <p className="font-medium mb-1">Project Explore NO:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Vende ni alquila expedientes educativos</li>
            <li>Divulga expedientes educativos a anunciantes ni a empresas de mercadotecnia</li>
            <li>Usa expedientes educativos para crear perfiles con fines no educativos</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            7. Apoyo para la notificación anual
          </h2>
          <p>
            FERPA exige que las escuelas notifiquen cada año a los padres sobre
            sus derechos bajo FERPA. Project Explore ayuda a los distritos
            contratantes a cumplir esta obligación con un texto estándar que
            describe la función de Project Explore y sus prácticas de datos, y
            que los distritos pueden incluir en su notificación anual de FERPA.
            Comunícate con el soporte de Project Explore para pedir este texto.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            8. Contacto
          </h2>
          <p className="mb-2">
            Para consultas relacionadas con FERPA, incluidas las solicitudes
            para inspeccionar, revisar o corregir expedientes educativos:
          </p>
          <p>
            <strong>Justin Ghiglia, contacto de privacidad</strong>
            <br />
            Startup Smartup
            <br />
            2380 California Street #402, San Francisco, CA 94115
            <br />
            <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
              privacy@startupsmartup.com
            </a>
          </p>
        </div>

        <div className="text-xs text-gray-500">
          Fecha de entrada en vigor: 27 de mayo de 2026
          <br />
          Referencias cruzadas:{" "}
          <Link to="/privacy" className="text-indigo-600 underline">Política de privacidad</Link>{" | "}
          <Link to="/privacy#coppa" className="text-indigo-600 underline">Anexo de COPPA</Link>{" | "}
          <Link to="/legal/sopipa" className="text-indigo-600 underline">Reconocimiento de SOPIPA</Link>{" | "}
          <Link to="/legal/dpa" className="text-indigo-600 underline">Acuerdo de procesamiento de datos</Link>
        </div>
      </section>
    </div>
  );
}

export default function FerpaStatementPage() {
  return useUiLang() === "es" ? <FerpaStatementPageEs /> : <FerpaStatementPageEn />;
}
